import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';

const Pricing = ({ onBack }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const freeFeatures = [
    'AI-powered search that understands natural language',
    'Unlimited searches, no limits',
    'Save up to 3 searches as scouts',
    'Email alerts when new properties match',
    'Search history and analytics',
    'Route calculation (walking/cycling distances)',
    'Manual filters if you prefer'
  ];

  return (
    <div className="min-h-screen bg-white pt-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12 text-center"
        >
          <motion.button
            type="button"
            whileHover={{ x: -3 }}
            whileTap={{ scale: 0.95 }}
            onClick={(e) => {
              e.preventDefault();
              onBack();
            }}
            className="mb-8 p-2 hover:bg-gray-100 rounded-full transition-colors inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowRight className="w-5 h-5 rotate-180" />
            <span className="hidden sm:inline">Back</span>
          </motion.button>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-4"
          >
            Pricing
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto"
          >
            Completely free. No strings attached.
          </motion.p>
        </motion.div>

        {/* Pricing Card */}
        <div className="max-w-2xl mx-auto mb-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            whileHover={{ scale: 1.02 }}
            className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-gray-200 shadow-sm hover:shadow-lg transition-all"
          >
            <div className="mb-8">
              <motion.h2
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2"
              >
                Free
              </motion.h2>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="flex items-baseline gap-2 mb-3"
              >
                <span className="text-4xl sm:text-5xl font-bold text-gray-900">$0</span>
                <span className="text-gray-600 text-lg sm:text-xl">forever</span>
              </motion.div>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="text-gray-600"
              >
                No credit card. No hidden fees. Just works.
              </motion.p>
            </div>
            <ul className="space-y-4">
              {freeFeatures.map((feature, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7 + index * 0.05 }}
                  className="flex items-start gap-3"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.8 + index * 0.05, type: 'spring' }}
                  >
                    <Check className="w-5 h-5 text-gray-900 flex-shrink-0 mt-0.5" />
                  </motion.div>
                  <span className="text-gray-700 text-sm sm:text-base">{feature}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          className="bg-gray-50 rounded-xl sm:rounded-2xl p-6 sm:p-8 border border-gray-200"
        >
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            What you get
          </h3>
          <div className="space-y-4 text-gray-700 text-sm sm:text-base">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.3 }}
            >
              <strong className="text-gray-900">3 active scouts:</strong> Save up to 3 searches. 
              We email you when new places match.
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.4 }}
            >
              <strong className="text-gray-900">Unlimited searches:</strong> Search as much as you want. 
              No limits.
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5 }}
            >
              <strong className="text-gray-900">AI-powered:</strong> We understand natural language. 
              Just tell us what you want.
            </motion.p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Pricing;
